import $ from "jquery";
import Snap from "snapsvg-cjs-ts";
import { Checkbox, ColorPicker, InputNumber } from "./Form";
import { type MapModel, getMapMeta, hasSameMapArea } from "./MapMeta";
import { readColor, readFlag, readNumber } from "./UrlState";

const VIEW_PRESETS: Record<
    string,
    { xmin: number; xmax: number; ymin: number; deg: number }
> = {
    japan: { xmin: -3475, xmax: 1955.58, ymin: 5098, deg: 0 },
    honshu: { xmin: -2101.869, xmax: 1319.183, ymin: 5244.788, deg: 7.5 },
    "okinawa-kanto": {
        xmin: -2729.088,
        xmax: 691.964,
        ymin: 6266.4,
        deg: 5,
    },
    "kyushu-kanto": { xmin: -1497, xmax: 423, ymin: 6171, deg: 5 },
    kanto: { xmin: -385.543, xmax: 293.28, ymin: 6325.002, deg: 2.5 },
    south: { xmin: -5785.035, xmax: 5076.125, ymin: 4223.712, deg: 0 },
    north: { xmin: -5296.599, xmax: 4379.595, ymin: 3089, deg: 0 },
    "far-east": { xmin: -8056.523, xmax: 7303.477, ymin: -265.21, deg: 0 },
    nh1: { xmin: -13872.507, xmax: 13495.901, ymin: -6989.37, deg: 0 },
    nh2: { xmin: -12189.403, xmax: 12193.077, ymin: -6775.99, deg: 25 },
    dust: { xmin: -5975.24, xmax: 2645.269, ymin: 3529.042, deg: 0 },
};
const WIDE_VIEW_PRESETS = [
    "south",
    "north",
    "dust",
    "far-east",
    "nh1",
    "nh2",
] as const;
const ASIA_VIEW_PRESETS = new Set(["south", "north", "dust"]);

export class MapSize {
    constructor() {
        window.addEventListener("resize", () => this.resize());
        this.resize();
    }
    private resize() {
        const wtmp = $(window).width()! - 260;
        const htmp = $(window).height()! - 200;
        const width = Math.min(
            Math.max(Math.min(wtmp, (htmp * 16) / 9.0), 720),
            1920,
        );
        const height = Math.min(
            Math.max(Math.min((wtmp * 9) / 16.0, htmp), 405),
            1080,
        );
        $("#svg_main").attr({ width, height });
        $("#svg_over").attr({ width, height });
        $("#main_footer").css({ width });
    }
}

export class MapLoader {
    private static readonly svg = new Map<string, string>();
    // タスクG: bm=tiles のとき下地はベクトルタイル (BasemapTiles) 供給。SVG は読まない。
    static tilesMode = false;
    private currentBasemap = "";
    static async fetch(model: MapModel) {
        if (MapLoader.tilesMode) {
            return;
        }
        const name = getMapMeta(model).basemap;
        if (this.svg.has(name)) {
            return;
        }
        const response = await fetch(name);
        if (!response.ok) {
            throw new Error(`${name}: HTTP ${response.status}`);
        }
        this.svg.set(name, await response.text());
    }
    constructor(model: MapModel) {
        $("#main").on("contextmenu", () => false);
        this.installModel(model);
    }

    async setModel(model: MapModel) {
        await MapLoader.fetch(model);
        return this.installModel(model);
    }
    private installModel(model: MapModel) {
        const meta = getMapMeta(model);
        if (MapLoader.tilesMode) {
            // タイル下地 (タスクG): SVG 差し替えと #clip_coast は BasemapTiles が担う。
            // モデル別の描画範囲 (extent) だけ従来どおり更新する。
            this.applyExtent(meta);
            this.currentBasemap = "tiles";
            return false;
        }
        if (meta.basemap === this.currentBasemap) {
            return false;
        }
        const source = MapLoader.svg.get(meta.basemap);
        if (!source) {
            throw new Error(`${meta.basemap} is not loaded.`);
        }
        document.querySelector("#svg_defs > [data-gpv-basemap]")?.remove();
        const xmldoc = new DOMParser().parseFromString(source, "image/svg+xml");
        const svg = xmldoc.documentElement;
        svg.setAttribute("data-gpv-basemap", meta.basemap);
        document.querySelector("#svg_defs")!.appendChild(svg);
        const clipCoast = document.querySelector(
            "#clip_coast",
        ) as SVGClipPathElement;
        clipCoast.replaceChildren();
        for (const node of document.querySelector("#japan_coast")
            ?.childNodes!) {
            clipCoast.appendChild(node.cloneNode(true)!);
        }
        this.applyExtent(meta);
        this.currentBasemap = meta.basemap;
        return true;
    }
    private applyExtent(meta: ReturnType<typeof getMapMeta>) {
        const extent = {
            x: String(meta.x0),
            y: String(meta.y0),
            width: String(meta.width),
            height: String(meta.height),
        };
        const interaction = document.querySelector<SVGRectElement>(
            "#screen_rotate > rect",
        );
        for (const node of [
            interaction,
            ...document.querySelectorAll<SVGImageElement>(
                "#region_image_0, #region_image_1, #region_image_2",
            ),
        ]) {
            if (!node) {
                continue;
            }
            for (const [name, value] of Object.entries(extent)) {
                node.setAttribute(name, value);
            }
        }
    }
}

export class MapView {
    private readonly SC = Snap("#screen");
    private readonly RG = Snap("#screen_rotate");
    private readonly SC2 = Snap("#screen2");
    private readonly RG2 = Snap("#screen2_rotate");

    private xmin = -3475;
    private xmax = 1955.58;
    private ymin = 5098;
    private deg = 0;
    private mapLv: number; // -1～23
    private width: number;
    private height: number;

    private dx: number;
    private dy: number;
    private scale: number;

    private zoomCallback = () => {};
    private dragCallback = () => {};
    private rotateCallback = () => {};

    private fixMode: 0 | 1 | 2 = 0;
    private showParticle = () => {};
    private hideParticle = () => {};
    private resetParticle = () => {};

    constructor(
        private readonly params: URLSearchParams,
        private model: MapModel,
    ) {
        $("#svg_main").on("wheel", (event) => {
            event.preventDefault();
            const e: WheelEvent = event.originalEvent as WheelEvent;
            this.zoom(e.deltaY > 0 ? -1 : +1);
            this.zoomCallback();
        });
        $("#btn_zoom").on("click", () => {
            this.zoom(+1);
            this.zoomCallback();
        });
        $("#btn_pan").on("click", () => {
            this.zoom(-1);
            this.zoomCallback();
        });
        $("#btn_rotate_r").on("click", () => {
            this.deg += 2.5;
            this.update();
            this.rotateCallback();
        });
        $("#btn_rotate_l").on("click", () => {
            this.deg -= 2.5;
            this.update();
            this.rotateCallback();
        });
        $("#input_viewR").on("change", () => {
            this.deg = Number($("#input_viewR").val());
            this.update();
            this.rotateCallback();
        });
        this.RG.drag(
            (dx, dy, _x, _y, e) => this.dragMove(dx, dy, e),
            (_x, _y, e) => this.dragStart(0, 0, e),
            (e) => this.dragEnd(e),
        );
        this.loadParams();
        this.updatePresetAvailability();
    }
    loadParams() {
        const initialView = getMapMeta(this.model).initialView;
        const defaultXmin = initialView.x;
        const defaultXmax = initialView.x + initialView.width;
        const xmin = readNumber(this.params, "vw_lonmin", defaultXmin);
        const xmax = readNumber(this.params, "vw_lonmax", defaultXmax);
        const validWidth = xmax > xmin && xmax - xmin <= 100000;
        this.xmin = validWidth ? xmin : defaultXmin;
        this.xmax = validWidth ? xmax : defaultXmax;
        this.ymin = readNumber(this.params, "vw_latmin", initialView.y);
        this.deg = readNumber(
            this.params,
            "vw_deg",
            initialView.deg,
            -3600,
            3600,
        );
        this.width = this.xmax - this.xmin;
        this.height = (this.width / 16) * 9;
        this.mapLv =
            10 - Math.floor(Math.log(this.width / 1920) * Math.LOG2E * 6 + 0.5);
        this.update();
    }
    setModel(model: MapModel, resetView: boolean) {
        const changed = !hasSameMapArea(this.model, model);
        this.model = model;
        this.updatePresetAvailability();
        if (changed && resetView) {
            this.useModelInitialView();
        }
        return changed;
    }
    private useModelInitialView() {
        const view = getMapMeta(this.model).initialView;
        this.xmin = view.x;
        this.xmax = view.x + view.width;
        this.ymin = view.y;
        this.deg = view.deg;
        this.width = view.width;
        this.height = (this.width / 16) * 9;
        this.mapLv =
            10 - Math.floor(Math.log(this.width / 1920) * Math.LOG2E * 6 + 0.5);
        this.update();
    }
    saveParams() {
        this.params.set("vw_lonmin", this.xmin.toFixed(3));
        this.params.set("vw_lonmax", this.xmax.toFixed(3));
        this.params.set("vw_latmin", this.ymin.toFixed(3));
        this.params.set("vw_deg", String(this.deg));
    }
    private update() {
        const viewBox = [this.xmin, this.ymin, this.width, this.height];
        this.SC.attr({ viewBox });
        this.SC2.attr({ viewBox });
        const cx = this.xmin + this.width * 0.5;
        const cy = this.ymin + this.height * 0.5;
        this.RG.attr({ transform: `rotate(${this.deg},${cx},${cy})` });
        this.RG2.attr({ transform: `rotate(${this.deg},${cx},${cy})` });
        $("#input_viewR").val(this.deg);
    }
    private dragStart(_x: number, _y: number, e: MouseEvent) {
        if (e.button !== 0) {
            return;
        } //左ドラッグのみ反応
        if (this.fixMode === 1) {
            return;
        }
        if (this.fixMode === 2) {
            this.hideParticle();
        }
        this.dx = 0;
        this.dy = 0;
        this.scale = this.getDisplayScale();
    }
    private dragMove(dx: number, dy: number, e: MouseEvent) {
        if (e.button !== 0) {
            return;
        } //左ドラッグのみ反応
        if (this.fixMode === 1) {
            return;
        }
        this.dx = dx * this.scale;
        this.dy = dy * this.scale;
        const viewBox = [
            this.xmin - this.dx,
            this.ymin - this.dy,
            this.width,
            this.height,
        ];
        this.SC.attr({ viewBox });
        this.SC2.attr({ viewBox });
    }
    private dragEnd(e: MouseEvent) {
        if (e.button !== 0) {
            return;
        } //左ドラッグのみ反応
        if (this.fixMode === 1) {
            return;
        }
        if (this.fixMode === 2) {
            if (confirm("表示中の風粒子がリセットされますがよろしいですか？")) {
                this.resetParticle();
            } else {
                this.update();
                this.showParticle();
                return;
            }
        }
        const cos = Math.cos((this.deg / 180.0) * Math.PI);
        const sin = Math.sin((this.deg / 180.0) * Math.PI);
        const dx2 = this.dx * cos + this.dy * sin;
        const dy2 = -this.dx * sin + this.dy * cos;
        this.xmin -= dx2;
        this.ymin -= dy2;
        this.update();
        this.dragCallback();
    }
    private minMapLv() {
        // 従来の下限 -1 は日本域 (幅5600km) 前提。広域モデルでは「メタ幅の1.25倍」相当と
        // 「初期画角から2段引き」の緩い方まで許す。日本域は従来と同じ -1 になる。
        const meta = getMapMeta(this.model);
        const fromWidth =
            10 -
            Math.floor(
                Math.log((meta.width * 1.25) / 1920) * Math.LOG2E * 6 + 0.5,
            );
        const initialLv =
            10 -
            Math.floor(
                Math.log(meta.initialView.width / 1920) * Math.LOG2E * 6 + 0.5,
            );
        return Math.min(fromWidth, initialLv - 2);
    }
    private zoom(d: number) {
        if (this.mapLv + d > 23 || this.mapLv + d < this.minMapLv()) {
            return;
        }
        this.mapLv += d;
        const center_x = this.xmin + this.width / 2;
        const center_y = this.ymin + this.height / 2;
        this.width = 1920 * Math.pow(2, (-1 * (this.mapLv - 10)) / 6); // Lv4=>3840, Lv10=>1920, Lv16=>960
        this.height = (this.width / 16) * 9;
        this.xmin = center_x - this.width / 2;
        this.xmax = center_x + this.width / 2;
        this.ymin = center_y - this.height / 2;
        this.update();
    }
    getScreenScale() {
        return this.width / 1920;
    }
    getDisplayScale() {
        return this.width / Number($("#svg_main").attr("width"));
    }
    getViewBox() {
        return {
            x: this.xmin,
            y: this.ymin,
            width: this.width,
            height: this.height,
            deg: this.deg,
        };
    }
    onZoom(callback: () => void) {
        this.zoomCallback = callback;
    }
    onDrag(callback: () => void) {
        this.dragCallback = callback;
    }
    onRotate(callback: () => void) {
        this.rotateCallback = callback;
    }
    setFixMode(mode: 0 | 1 | 2) {
        this.fixMode = mode;
    }
    registerShowParticle(callback: () => void) {
        this.showParticle = callback;
    }
    registerHideParticle(callback: () => void) {
        this.hideParticle = callback;
    }
    registerResetParticle(callback: () => void) {
        this.resetParticle = callback;
    }
    usePreset(preset: string) {
        const view = VIEW_PRESETS[preset];
        if (!view) {
            return true;
        }
        if (!this.isPresetAvailable(preset)) {
            return false;
        }
        this.xmin = view.xmin;
        this.xmax = view.xmax;
        this.ymin = view.ymin;
        this.deg = view.deg;
        this.width = this.xmax - this.xmin;
        this.height = (this.width / 16) * 9;
        this.mapLv =
            10 - Math.floor(Math.log(this.width / 1920) * Math.LOG2E * 6 + 0.5);
        this.update();
        return true;
    }
    private isPresetAvailable(preset: string) {
        if (
            !WIDE_VIEW_PRESETS.includes(
                preset as (typeof WIDE_VIEW_PRESETS)[number],
            )
        ) {
            return true;
        }
        if (this.model === "GSL") {
            return true;
        }
        if (this.model === "GSA" || this.model === "MSG") {
            return ASIA_VIEW_PRESETS.has(preset);
        }
        return false;
    }
    private updatePresetAvailability() {
        for (const preset of WIDE_VIEW_PRESETS) {
            const link = document.querySelector<HTMLElement>(
                `#preset_${preset}`,
            );
            const available = this.isPresetAvailable(preset);
            link?.classList.toggle("view-preset-unavailable", !available);
            link?.toggleAttribute("inert", !available);
            link?.setAttribute("aria-disabled", String(!available));
        }
    }
}

// 種別連動の背景(海・湖)色 — 旧版 prop.bgcol の移植。
// 手動指定 (URL の色指定・ピッカー操作・配色プリセット) が無い間はこれを適用する
const BACK_COLOR_BY_VARIABLE: Record<string, string> = {
    GSMR: "#eeeeff", GSMRG: "#eeeeff", GSMRC: "#223388", GSMC: "#223388", GSMSj: "#ddddff", GSMSh: "#ddddff", GSMSv: "#ddddff", GSMN: "#333333", GSMV: "#2244dd",
    GS5R: "#eeeeff", GS5Sj: "#ddddff", GS5Sh: "#ddddff", GS5N: "#333333",
    GSAR: "#eeeeff", GSASj: "#ddddff", GSASh: "#ddddff", GSASv: "#ddddff", GSAN: "#333333",
    GSLR: "#eeeeff", GSLN: "#333333",
    MSGL: "#eeeeff",
    MSMR: "#eeeeff", MSMRG: "#eeeeff", MSMRC: "#223388", MSMT: "#eeeeff", MSMSj: "#ddddff", MSMSh: "#ddddff", MSMSv: "#ddddff", MSMN: "#333333", MSMV: "#2244dd",
    LFMR: "#eeeeff", LFMRC: "#223388", LFMT: "#eeeeff", LFMSj: "#ddddff", LFMSh: "#ddddff", LFMSv: "#ddddff", LFMN: "#333333",
    CWMH: "#ddddff",
    QMAR: "#f1f1f1", QMAN: "#f1f1f1", QMASj: "#dddddd", QMASh: "#dddddd",
}; // biome-ignore format:

// 明示的な色指定か (旧版と同じく "def" 等の非カラー値は種別連動を意味する)
function isExplicitColor(value: string | null) {
    return value !== null && /^#[0-9a-f]{6}([0-9a-f]{2})?$/i.test(value);
}

export class MapStyle {
    private readonly backColor: ColorPicker;
    private backColorAuto: boolean;
    private getShadeVariable: () => string = () => "";
    private readonly landJapanColor: ColorPicker;
    private readonly coastJapanWidth: InputNumber;
    private readonly coastJapanColor: ColorPicker;
    private readonly edgeJapanWidth: InputNumber;
    private readonly edgeJapanColor: ColorPicker;
    private readonly prefWidth: InputNumber;
    private readonly prefColor: ColorPicker;
    private readonly dispHokkaidoBoundary: Checkbox;
    private readonly landForeignColor: ColorPicker;
    private readonly coastForeignWidth: InputNumber;
    private readonly coastForeignColor: ColorPicker;
    // 国境 (GPV-NH と同様に海外海岸線と別設定。2026-07-22 発注者指示)
    private readonly borderForeignWidth: InputNumber;
    private readonly borderForeignColor: ColorPicker;
    private readonly lonlatWidth: InputNumber;
    private readonly lonlatColor: ColorPicker;

    private readonly bg = Snap("#bg");
    private readonly foreign_coast_a = Snap("#foreign_coast_a");
    private readonly lake_a = Snap("#lake_a");
    private readonly lonlat_a = Snap("#lonlat_a");
    private readonly japan_coast_a = Snap("#japan_coast_a");
    private readonly japan_hokkaido_f = Snap("#japan_hokkaido_f");
    private readonly lake_f = Snap("#lake_f");
    private readonly japan_coast_f = Snap("#japan_coast_f");
    private readonly japan_pref_f = Snap("#japan_pref_f");
    // 縁 (2026-08-02): 各線の下に同一図形を参照する縁用 <use>
    private readonly japan_coast_e = Snap("#japan_coast_e");
    private readonly japan_pref_e = Snap("#japan_pref_e");
    private readonly japan_hokkaido_e = Snap("#japan_hokkaido_e");
    private readonly foreign_coast_f = Snap("#foreign_coast_f");
    private readonly foreign_lake_a = Snap("#foreign_lake_a");
    private readonly foreign_lake_f = Snap("#foreign_lake_f");
    private readonly foreign_border_f = Snap("#foreign_border_f");

    constructor(
        private readonly params: URLSearchParams,
        private readonly mapView: MapView,
    ) {
        this.backColorAuto = !isExplicitColor(this.params.get("sty_backC"));
        const backColor = readColor(this.params, "sty_backC", "#eeeeff");
        this.backColor = new ColorPicker("#colpic_back", backColor, [
            "#f1f1f1",
            "#173c61",
            "#333333",
            "#223388",
            "#2244dd",
            "#4466ff",
            "#ddddff",
            "#eeeeff",
            "#ffffff00",
        ]);
        this.backColor.onChange(() => {
            this.backColorAuto = false; // 手動指定 — 以後は種別連動しない
            this.updateBackColor();
        });

        const swatches = [
            "#77ee66",
            "#66bb66",
            "#336633",
            "#669966",
            "#223388",
            "#333333",
            "#999999",
            "#cccccc",
            "#ffffff00",
        ];

        const landJapanColor = readColor(this.params, "sty_landJ", "#77ee66");
        this.landJapanColor = new ColorPicker(
            "#colpic_landJ",
            landJapanColor,
            swatches,
        );
        this.landJapanColor.onChange(() => this.updateLandJapanColor());

        this.coastJapanWidth = new InputNumber("#input_coastJW", {
            value: 2,
            min: 0,
            step: 0.2,
        });
        this.coastJapanWidth.onChange(() => this.updateCoastJapanWidth());
        this.coastJapanWidth.value = readNumber(
            this.params,
            "sty_coastJW",
            2,
            0,
            100,
        );

        const coastJapanColor = readColor(this.params, "sty_coastJ", "#336633");
        this.coastJapanColor = new ColorPicker(
            "#colpic_coastJ",
            coastJapanColor,
            swatches,
        );
        this.coastJapanColor.onChange(() => this.updateCoastJapanColor());

        // 縁 (2026-08-02 発注者指示): 日本海岸線・県境(支庁境)の両サイドへ
        // 追加する幅と色。0で非表示 (既定)
        this.edgeJapanWidth = new InputNumber("#input_edgeJW", {
            value: 0,
            min: 0,
            step: 0.2,
        });
        this.edgeJapanWidth.onChange(() => this.updateEdgeJapan());
        this.edgeJapanWidth.value = readNumber(
            this.params,
            "sty_edgeJW",
            0,
            0,
            100,
        );
        const edgeJapanColor = readColor(this.params, "sty_edgeJ", "#fcfcfc");
        this.edgeJapanColor = new ColorPicker(
            "#colpic_edgeJ",
            edgeJapanColor,
            swatches,
        );
        this.edgeJapanColor.onChange(() => this.updateEdgeJapan());

        this.prefWidth = new InputNumber("#input_prefW", {
            value: 1,
            min: 0,
            step: 0.2,
        });
        this.prefWidth.onChange(() => this.updatePrefWidth());
        this.prefWidth.value = readNumber(this.params, "sty_prefW", 1, 0, 100);

        const prefColor = readColor(this.params, "sty_prefC", "#669966");
        this.prefColor = new ColorPicker("#colpic_pref", prefColor, swatches);
        this.prefColor.onChange(() => this.updatePrefColor());

        const dispHokkaidoBoundary = readFlag(this.params, "sty_dispHB", "0");
        this.dispHokkaidoBoundary = new Checkbox("#cb_dispHB");
        this.dispHokkaidoBoundary.value = dispHokkaidoBoundary;
        this.dispHokkaidoBoundary.onChange(() => this.updatePrefWidth());

        const landForeignColor = readColor(
            this.params,
            "sty_landFC",
            "#66bb66",
        );
        this.landForeignColor = new ColorPicker(
            "#colpic_landF",
            landForeignColor,
            swatches,
        );
        this.landForeignColor.onChange(() => this.updateLandForeignColor());

        this.coastForeignWidth = new InputNumber("#input_coastFW", {
            value: 1.4,
            min: 0,
            step: 0.2,
        });
        this.coastForeignWidth.onChange(() => this.updateCoastForeignWidth());
        this.coastForeignWidth.value = readNumber(
            this.params,
            "sty_coastFW",
            1.4,
            0,
            100,
        );

        const coastForeignColor = readColor(
            this.params,
            "sty_coastFC",
            "#336633",
        );
        this.coastForeignColor = new ColorPicker(
            "#colpic_coastF",
            coastForeignColor,
            swatches,
        );
        this.coastForeignColor.onChange(() => this.updateCoastForeignColor());

        this.borderForeignWidth = new InputNumber("#input_borderFW", {
            value: 0,
            min: 0,
            step: 0.2,
        });
        this.borderForeignWidth.onChange(() => this.updateBorderForeignWidth());
        this.borderForeignWidth.value = readNumber(
            this.params,
            "sty_borderFW",
            0,
            0,
            100,
        );
        const borderForeignColor = readColor(
            this.params,
            "sty_borderFC",
            "#669966",
        );
        this.borderForeignColor = new ColorPicker(
            "#colpic_borderF",
            borderForeignColor,
            swatches,
        );
        this.borderForeignColor.onChange(() => this.updateBorderForeignColor());

        this.lonlatWidth = new InputNumber("#input_lonlatW", {
            value: 0.6,
            min: 0,
            step: 0.2,
        });
        this.lonlatWidth.onChange(() => this.updateLonlatWidth());
        this.lonlatWidth.value = readNumber(
            this.params,
            "sty_lonlatW",
            0.6,
            0,
            100,
        );

        const lonlatColor = readColor(this.params, "sty_lonlatC", "#cccccc");
        this.lonlatColor = new ColorPicker(
            "#colpic_lonlat",
            lonlatColor,
            swatches,
        );
        this.lonlatColor.onChange(() => this.updateLonlatColor());

        this.updateColor();
        this.updateWidth();
    }
    // 種別を参照できるようにする (ShadeSelector は MapStyle より後に生成されるため注入)
    setShadeVariableGetter(getter: () => string) {
        this.getShadeVariable = getter;
        this.applyLinkedBackColor();
    }
    onShadeVariableChanged() {
        this.applyLinkedBackColor();
    }
    private applyLinkedBackColor() {
        if (!this.backColorAuto) {
            return;
        }
        const color = BACK_COLOR_BY_VARIABLE[this.getShadeVariable()];
        if (color) {
            this.backColor.setColor(color);
            this.updateBackColor();
        }
    }
    loadParams() {
        this.backColorAuto = !isExplicitColor(this.params.get("sty_backC"));
        this.backColor.setColor(readColor(this.params, "sty_backC", "#eeeeff"));
        this.landJapanColor.setColor(
            readColor(this.params, "sty_landJ", "#77ee66"),
        );
        this.coastJapanWidth.value = readNumber(
            this.params,
            "sty_coastJW",
            2,
            0,
            100,
        );
        this.coastJapanColor.setColor(
            readColor(this.params, "sty_coastJ", "#336633"),
        );
        this.edgeJapanWidth.value = readNumber(
            this.params,
            "sty_edgeJW",
            0,
            0,
            100,
        );
        this.edgeJapanColor.setColor(
            readColor(this.params, "sty_edgeJ", "#fcfcfc"),
        );
        this.prefWidth.value = readNumber(this.params, "sty_prefW", 1, 0, 100);
        this.prefColor.setColor(readColor(this.params, "sty_prefC", "#669966"));
        this.dispHokkaidoBoundary.value = readFlag(
            this.params,
            "sty_dispHB",
            "0",
        );
        this.landForeignColor.setColor(
            readColor(this.params, "sty_landFC", "#66bb66"),
        );
        this.coastForeignWidth.value = readNumber(
            this.params,
            "sty_coastFW",
            1.4,
            0,
            100,
        );
        this.coastForeignColor.setColor(
            readColor(this.params, "sty_coastFC", "#336633"),
        );
        this.borderForeignWidth.value = readNumber(
            this.params,
            "sty_borderFW",
            0,
            0,
            100,
        );
        this.borderForeignColor.setColor(
            readColor(this.params, "sty_borderFC", "#669966"),
        );
        this.lonlatWidth.value = readNumber(
            this.params,
            "sty_lonlatW",
            0.6,
            0,
            100,
        );
        this.lonlatColor.setColor(
            readColor(this.params, "sty_lonlatC", "#cccccc"),
        );
        this.updateColor();
        this.updateWidth();
        this.applyLinkedBackColor();
    }
    saveParams() {
        // 種別連動中は旧版と同じく "def" を保存する (色固定と区別する)
        this.params.set(
            "sty_backC",
            this.backColorAuto ? "def" : this.backColor.getColor(),
        );
        this.params.set("sty_landJ", this.landJapanColor.getColor());
        this.params.set("sty_coastJW", String(this.coastJapanWidth.value));
        this.params.set("sty_coastJ", this.coastJapanColor.getColor());
        this.params.set("sty_edgeJW", String(this.edgeJapanWidth.value));
        this.params.set("sty_edgeJ", this.edgeJapanColor.getColor());
        this.params.set("sty_prefW", String(this.prefWidth.value));
        this.params.set("sty_prefC", this.prefColor.getColor());
        this.params.set("sty_dispHB", this.dispHokkaidoBoundary.value);
        this.params.set("sty_landFC", this.landForeignColor.getColor());
        this.params.set("sty_coastFW", String(this.coastForeignWidth.value));
        this.params.set("sty_coastFC", this.coastForeignColor.getColor());
        this.params.set("sty_borderFW", String(this.borderForeignWidth.value));
        this.params.set("sty_borderFC", this.borderForeignColor.getColor());
        this.params.set("sty_lonlatW", String(this.lonlatWidth.value));
        this.params.set("sty_lonlatC", this.lonlatColor.getColor());
    }
    private updateColor() {
        this.updateBackColor();
        this.updateLandJapanColor();
        this.updateCoastJapanColor();
        this.updatePrefColor();
        this.updateLandForeignColor();
        this.updateCoastForeignColor();
        this.updateBorderForeignColor();
        this.updateLonlatColor();
    }
    updateWidth() {
        this.updateCoastJapanWidth();
        this.updateCoastForeignWidth();
        this.updateBorderForeignWidth();
        this.updatePrefWidth();
        this.updateLonlatWidth();
    }
    private updateBackColor() {
        const { color, opacity } = this.backColor.getColorObject();
        this.bg.attr({ fill: color, fillOpacity: opacity });
        this.lake_a.attr({ fill: color, fillOpacity: opacity });
        this.foreign_lake_a.attr({ fill: color, fillOpacity: opacity });
    }
    private updateLandJapanColor() {
        const { color, opacity } = this.landJapanColor.getColorObject();
        this.japan_coast_a.attr({ fill: color, fillOpacity: opacity });
    }
    private updateLandForeignColor() {
        const { color, opacity } = this.landForeignColor.getColorObject();
        this.foreign_coast_a.attr({ fill: color, fillOpacity: opacity });
    }
    private updateCoastJapanColor() {
        const { color, opacity } = this.coastJapanColor.getColorObject();
        this.japan_coast_f.attr({ stroke: color, strokeOpacity: opacity });
        this.lake_f.attr({ stroke: color, strokeOpacity: opacity });
    }
    private updateCoastJapanWidth() {
        const scale = this.mapView.getScreenScale();
        this.japan_coast_f.attr({
            strokeWidth: this.coastJapanWidth.value * scale,
        });
        this.lake_f.attr({
            strokeWidth: this.coastJapanWidth.value * scale * 0.5,
        });
        this.updateEdgeJapan();
    }
    // 縁 (2026-08-02): 海岸線・県境・支庁境の下に同一図形を
    // 「本体幅 + 縁幅×2 (両サイド)」で描く。幅0なら非表示
    private updateEdgeJapan() {
        const edge = this.edgeJapanWidth.value;
        if (edge <= 0) {
            this.japan_coast_e.attr({ display: "none" });
            this.japan_pref_e.attr({ display: "none" });
            this.japan_hokkaido_e.attr({ display: "none" });
            return;
        }
        const scale = this.mapView.getScreenScale();
        const { color, opacity } = this.edgeJapanColor.getColorObject();
        const add = edge * 2;
        this.japan_coast_e.attr({
            display: "inline",
            stroke: color,
            strokeOpacity: opacity,
            strokeWidth: (this.coastJapanWidth.value + add) * scale,
        });
        this.japan_pref_e.attr({
            display: "inline",
            stroke: color,
            strokeOpacity: opacity,
            strokeWidth: (this.prefWidth.value + add) * scale,
        });
        // 支庁境の縁は表示チェックへ追従 (非表示なら幅0で描かれない)
        this.japan_hokkaido_e.attr({
            display: "inline",
            stroke: color,
            strokeOpacity: opacity,
            strokeWidth:
                (this.prefWidth.value + add) *
                scale *
                Number(this.dispHokkaidoBoundary.value),
        });
    }
    private updateCoastForeignColor() {
        const { color, opacity } = this.coastForeignColor.getColorObject();
        this.foreign_coast_f.attr({ stroke: color, strokeOpacity: opacity });
        this.foreign_lake_f.attr({ stroke: color, strokeOpacity: opacity });
    }
    private updateCoastForeignWidth() {
        const scale = this.mapView.getScreenScale();
        this.foreign_coast_f.attr({
            strokeWidth: this.coastForeignWidth.value * scale,
        });
        this.foreign_lake_f.attr({
            strokeWidth: this.coastForeignWidth.value * scale,
        });
    }
    private updateBorderForeignColor() {
        const { color, opacity } = this.borderForeignColor.getColorObject();
        this.foreign_border_f.attr({ stroke: color, strokeOpacity: opacity });
    }
    private updateBorderForeignWidth() {
        const scale = this.mapView.getScreenScale();
        this.foreign_border_f.attr({
            strokeWidth: this.borderForeignWidth.value * scale,
        });
    }
    private updatePrefColor() {
        const { color, opacity } = this.prefColor.getColorObject();
        this.japan_pref_f.attr({ stroke: color, strokeOpacity: opacity });
        this.japan_hokkaido_f.attr({ stroke: color, strokeOpacity: opacity });
    }
    private updatePrefWidth() {
        const scale = this.mapView.getScreenScale();
        this.japan_pref_f.attr({ strokeWidth: this.prefWidth.value * scale });
        this.japan_hokkaido_f.attr({
            strokeWidth:
                this.prefWidth.value *
                scale *
                Number(this.dispHokkaidoBoundary.value),
        });
        this.updateEdgeJapan();
    }
    private updateLonlatColor() {
        const { color, opacity } = this.lonlatColor.getColorObject();
        this.lonlat_a.attr({ stroke: color, strokeOpacity: opacity });
    }
    private updateLonlatWidth() {
        const scale = this.mapView.getScreenScale();
        this.lonlat_a.attr({ strokeWidth: this.lonlatWidth.value * scale });
    }
    usePreset(preset: string) {
        switch (preset) {
            case "japan":
            case "honshu":
            case "okinawa-kanto":
            case "kyushu-kanto":
                this.prefWidth.value = 1.6;
                this.coastJapanWidth.value = 2.0;
                this.coastForeignWidth.value = 1.6;
                this.lonlatWidth.value = 0.6;
                break;
            case "kanto":
                this.prefWidth.value = 2.4;
                this.coastJapanWidth.value = 3.0;
                this.coastForeignWidth.value = 1.6;
                this.lonlatWidth.value = 0.6;
                break;
            case "south":
                this.prefWidth.value = 0.4;
                this.coastJapanWidth.value = 1.2;
                this.coastForeignWidth.value = 1.0;
                this.lonlatWidth.value = 0.6;
                break;
            case "north":
                this.prefWidth.value = 0.4;
                this.coastJapanWidth.value = 1.2;
                this.coastForeignWidth.value = 1.1;
                this.lonlatWidth.value = 0.6;
                break;
            case "far-east":
                this.prefWidth.value = 0;
                this.coastJapanWidth.value = 1.3;
                this.coastForeignWidth.value = 1.0;
                this.lonlatWidth.value = 0.6;
                break;
            case "nh1":
                this.prefWidth.value = 0;
                this.coastJapanWidth.value = 1.035;
                this.coastForeignWidth.value = 0.6;
                this.lonlatWidth.value = 0.6;
                break;
            case "nh2":
                this.prefWidth.value = 0;
                this.coastJapanWidth.value = 1.2;
                this.coastForeignWidth.value = 0.6;
                this.lonlatWidth.value = 0.6;
                break;
            case "dust":
                this.prefWidth.value = 0;
                this.coastJapanWidth.value = 2.0;
                this.coastForeignWidth.value = 1.8;
                this.borderForeignWidth.value = 1.6;
                this.lonlatWidth.value = 0.6;
                break;
            case "thunder":
                this.coastJapanWidth.value = 0.8;
                this.coastForeignWidth.value = 0.8;
                this.backColorAuto = false;
                this.backColor.setColor("#2244dd");
                this.landJapanColor.setColor("#77bb99");
                this.landForeignColor.setColor("#66bb66");
                this.prefColor.setColor("#0f2050");
                this.coastJapanColor.setColor("#0f2050");
                this.coastForeignColor.setColor("#333333");
                this.lonlatColor.setColor("#ffffff");
                break;
            case "trans":
                this.backColorAuto = false;
                this.backColor.setColor("#ffffff00");
                this.landJapanColor.setColor("#ffffff00");
                this.landForeignColor.setColor("#ffffff00");
                this.prefColor.setColor("#ffffff00");
                this.coastJapanColor.setColor("#ffffff00");
                this.coastForeignColor.setColor("#ffffff00");
                this.borderForeignColor.setColor("#ffffff00");
                this.lonlatColor.setColor("#ffffff00");
                break;
            case "light":
                this.backColorAuto = false;
                this.backColor.setColor("#eeeeff");
                this.landJapanColor.setColor("#77ee66");
                this.landForeignColor.setColor("#66bb66");
                this.prefColor.setColor("#669966");
                this.coastJapanColor.setColor("#336633");
                this.coastForeignColor.setColor("#336633");
                this.lonlatColor.setColor("#cccccc");
                break;
            case "darkwind":
                this.backColorAuto = false;
                this.backColor.setColor("#0a3c62");
                this.landJapanColor.setColor("#619c8d");
                this.landForeignColor.setColor("#86a49c");
                this.prefColor.setColor("#0f2050");
                this.coastJapanColor.setColor("#0f2050");
                this.coastForeignColor.setColor("#333333");
                this.lonlatColor.setColor("#25284E");
                break;
            default:
                return;
        }
        this.updateColor();
        // this.updateScale(); はApp.tsで呼び出す
    }
}
